import { apiErrorMessage } from "@/lib/api";
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Flame, Mail, Lock, User, Eye, EyeOff, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

function useNext() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  return params.get("next") || "/dashboard";
}

function GoogleButton({ next }) {
  const startGoogle = () => {
    const redirect = `${window.location.origin}/auth/google/callback?next=${encodeURIComponent(next)}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };
  return (
    <Button type="button" variant="outline" className="h-11 w-full gap-2 border-border" onClick={startGoogle}>
      <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.4-1.6 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.9 1.5l2.7-2.6C17.1 3.3 14.8 2.3 12 2.3 6.6 2.3 2.3 6.6 2.3 12S6.6 21.7 12 21.7c6.9 0 9.5-4.8 9.5-8.6 0-.6-.1-1-.1-1.5H12z"/></svg>
      Continue with Google
    </Button>
  );
}

export function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [show, setShow] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const next = useNext();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast({ title: "Welcome back", description: "Signed in to Furnace Design Lab" });
      navigate(next);
    } catch (err) {
      toast({ title: "Login failed", description: apiErrorMessage(err, "Try again"), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain">
      <Navbar />
      <div className="relative min-h-[80vh]">
        <div className="ember-glow absolute inset-0" />
        <div className="relative mx-auto flex max-w-5xl items-center gap-14 px-4 py-16 sm:px-6">
          <div className="hidden flex-1 lg:block">
            <span className="chip"><Flame className="h-3 w-3 text-[#4aa4ff]" /> Welcome back</span>
            <h1 className="display-font mt-4 text-5xl font-bold leading-tight">Sign in to your <span className="ember-text">Furnace</span>.</h1>
            <p className="mt-4 max-w-md text-muted-foreground">Pick up right where you left off. Halftones, AI vectors, watermark cleanup and world-class photo edits — all one tab.</p>
          </div>
          <form onSubmit={submit} className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-2xl shadow-black/40">
            <h2 className="display-font text-2xl font-bold">Sign in</h2>
            <p className="mt-1 text-sm text-muted-foreground">No account? <Link to="/signup" className="text-[#4aa4ff] hover:underline">Create one free</Link></p>
            <div className="mt-6"><GoogleButton next={next} /></div>
            <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground"><div className="h-px flex-1 bg-border" />or with email<div className="h-px flex-1 bg-border" /></div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1"><Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 pl-9" placeholder="you@shop.com" /></div>
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input id="password" type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 pl-9 pr-10" placeholder="••••••••" />
                  <button type="button" aria-label="toggle" onClick={() => setShow((s) => !s)} className="absolute right-3 top-3 text-muted-foreground hover:text-foreground">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                </div>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="btn-ember mt-6 h-11 w-full font-semibold">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}</Button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function SignupPage() {
  const { register } = useAuth();
  const [email, setEmail] = React.useState("");
  const [name, setName] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [show, setShow] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const next = useNext();

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast({ title: "Password too short", description: "Use at least 6 characters", variant: "destructive" });
      return;
    }
    if (/\.(test|example|invalid|localhost)$/i.test(email)) {
      toast({ title: "Use a real email domain", description: ".test / .example are reserved and will be rejected. Try a real address.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const refCode = new URLSearchParams(window.location.search).get("ref") || undefined;
      await register(email, password, name, refCode);
      toast({ title: "Welcome to Furnace Design Lab", description: "3 free exports queued this month." });
      navigate(next);
    } catch (err) {
      toast({ title: "Sign up failed", description: apiErrorMessage(err, "Try again"), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain">
      <Navbar />
      <div className="relative min-h-[80vh]">
        <div className="ember-glow absolute inset-0" />
        <div className="relative mx-auto flex max-w-5xl items-center gap-14 px-4 py-16 sm:px-6">
          <div className="hidden flex-1 lg:block">
            <span className="chip"><Flame className="h-3 w-3 text-[#4aa4ff]" /> Free · 3 exports/month</span>
            <h1 className="display-font mt-4 text-5xl font-bold leading-tight">Start printing <span className="ember-text">softer</span>.</h1>
            <p className="mt-4 max-w-md text-muted-foreground">Create your account and get 3 press-ready exports this month. Upgrade to Lifetime any time.</p>
            <ul className="mt-6 space-y-2 text-sm text-muted-foreground">
              <li>✓ No credit card</li>
              <li>✓ Art never leaves your browser</li>
              <li>✓ All 12 halftone styles available</li>
            </ul>
          </div>
          <form onSubmit={submit} className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-2xl shadow-black/40">
            <h2 className="display-font text-2xl font-bold">Create your account</h2>
            <p className="mt-1 text-sm text-muted-foreground">Have one? <Link to="/login" className="text-[#4aa4ff] hover:underline">Sign in</Link></p>
            <div className="mt-6"><GoogleButton next={next} /></div>
            <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground"><div className="h-px flex-1 bg-border" />or with email<div className="h-px flex-1 bg-border" /></div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Your name</Label>
                <div className="relative mt-1"><User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input id="name" value={name} onChange={(e) => setName(e.target.value)} className="h-11 pl-9" placeholder="Alex Printer" /></div>
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1"><Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 pl-9" placeholder="you@shop.com" /></div>
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input id="password" type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 pl-9 pr-10" placeholder="••••••••" />
                  <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-3 text-muted-foreground hover:text-foreground">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                </div>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="btn-ember mt-6 h-11 w-full font-semibold">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}</Button>
            <p className="mt-4 text-center text-xs text-muted-foreground">By continuing you agree to our terms and privacy policy.</p>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function GoogleCallback() {
  const { googleAuth } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  React.useEffect(() => {
    // Emergent auth returns session_id in URL fragment: #session_id=...&...
    const hash = window.location.hash?.slice(1) || "";
    const params = new URLSearchParams(hash);
    const search = new URLSearchParams(window.location.search);
    const sid = params.get("session_id") || search.get("session_id");
    const next = search.get("next") || "/dashboard";
    (async () => {
      if (!sid) {
        toast({ title: "Google auth failed", description: "Missing session", variant: "destructive" });
        navigate("/login");
        return;
      }
      try {
        await googleAuth(sid);
        toast({ title: "Signed in with Google" });
        navigate(next);
      } catch (err) {
        toast({ title: "Google sign-in error", description: apiErrorMessage(err, ""), variant: "destructive" });
        navigate("/login");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center gap-3 text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin text-[#4aa4ff]" /> Signing you in…
    </div>
  );
}
